import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
    TeoAskRequest,
    TeoAskResponse,
    TeoConversation,
    TeoConversationsResponse,
    TeoSourcesResponse,
    TeoProject,
    TeoProjectsResponse,
    Source
} from './teo.types';

@Injectable({ providedIn: 'root' })
export class TeoService {
    private readonly baseUrl = environment.config.apiUrl + '/v1/teo';
    private _isOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _previousUrl: string = '/home';

    /**
     * Generate masked/serialized ID (alphanumeric)
     * Format: xxxxx-xxxxxxxxxxx (5-11 characters)
     * Example: f84mv-ekd7jvs9844
     */
    static generateMaskedId(): string {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        const part1Length = 5;
        const part2Length = 11;

        let part1 = '';
        for (let i = 0; i < part1Length; i++) {
            part1 += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        let part2 = '';
        for (let i = 0; i < part2Length; i++) {
            part2 += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return `${part1}-${part2}`;
    }

    constructor(
        private _httpClient: HttpClient,
        private _router: Router
    ) {
        // Initialize with current URL
        const currentUrl = this._router.url;
        if (currentUrl !== '/' && currentUrl !== '') {
            this._previousUrl = currentUrl;
        }

        // Listen to route changes to sync isOpen state
        this._router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                const isTeoRoute = event.urlAfterRedirects.startsWith('/teo');

                // Save previous URL if not a Teo route
                if (!isTeoRoute && event.urlAfterRedirects !== '/' && event.urlAfterRedirects !== '') {
                    this._previousUrl = event.urlAfterRedirects;
                }

                // Update isOpen based on current route
                this._isOpen.next(isTeoRoute);
            });
    }

    /**
     * Send a question to Teo
     */
    ask(request: TeoAskRequest): Observable<TeoAskResponse> {
        return this._httpClient.post<TeoAskResponse>(`${this.baseUrl}/ask`, request);
    }

    /**
     * Get all conversations
     */
    getConversations(): Observable<TeoConversationsResponse> {
        return this._httpClient.get<TeoConversationsResponse>(`${this.baseUrl}/conversations`);
    }

    /**
     * Get a specific conversation by ID
     */
    getConversation(id: string): Observable<TeoConversation> {
        return this._httpClient.get<TeoConversation>(`${this.baseUrl}/conversations/${id}`);
    }

    /**
     * Create a new conversation
     */
    createConversation(title?: string): Observable<TeoConversation> {
        return this._httpClient.post<TeoConversation>(`${this.baseUrl}/conversations`, { title });
    }

    /**
     * Delete a conversation
     */
    deleteConversation(id: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/conversations/${id}`);
    }

    /**
     * Rename a conversation
     */
    renameConversation(id: string, title: string): Observable<TeoConversation> {
        return this._httpClient.patch<TeoConversation>(`${this.baseUrl}/conversations/${id}`, { title });
    }

    /**
     * Get all available sources (base library)
     */
    getSources(): Observable<TeoSourcesResponse> {
        return this._httpClient.get<TeoSourcesResponse>(`${this.baseUrl}/sources`);
    }

    /**
     * Toggle source active status
     */
    toggleSource(id: string, isActive: boolean): Observable<Source> {
        return this._httpClient.patch<Source>(`${this.baseUrl}/sources/${id}`, { isActive });
    }

    // ================================
    // MOCK DATA (for development)
    // ================================

    /**
     * Get mock sources for development
     */
    getMockSources(): Observable<TeoSourcesResponse> {
        const sources: Source[] = [
            {
                id: '1',
                name: 'Comentário Bíblico Matthew Henry',
                author: 'Matthew Henry',
                type: 'commentary',
                description: 'Comentário completo da Bíblia, versículo por versículo',
                isActive: true,
                addedAt: '2024-01-01'
            },
            {
                id: '2',
                name: 'Concordância de Strong',
                author: 'James Strong',
                type: 'concordance',
                description: 'Concordância exaustiva com números Strong',
                isActive: true,
                addedAt: '2024-01-01'
            },
            {
                id: '3',
                name: 'Teologia Sistemática',
                author: 'Wayne Grudem',
                type: 'theology',
                description: 'Introdução à doutrina bíblica',
                isActive: true,
                addedAt: '2024-01-01'
            },
            {
                id: '4',
                name: 'Dicionário Vine',
                author: 'W.E. Vine',
                type: 'dictionary',
                description: 'Dicionário expositivo de palavras do NT',
                isActive: false,
                addedAt: '2024-01-01'
            },
            {
                id: '5',
                name: 'Comentário Bíblico Beacon',
                author: 'Vários',
                type: 'commentary',
                description: 'Comentário em 10 volumes',
                isActive: true,
                addedAt: '2024-01-01'
            },
            {
                id: '6',
                name: 'Institutas da Religião Cristã',
                author: 'João Calvino',
                type: 'theology',
                description: 'Obra magna da teologia reformada',
                isActive: false,
                addedAt: '2024-01-01'
            }
        ];

        return of({ sources, total: sources.length });
    }

    /**
     * Get mock conversations for development
     */
    getMockConversations(): Observable<TeoConversationsResponse> {
        const conversations: TeoConversation[] = [
            {
                id: 'a5m8t-qw4np2zx7rl',
                title: 'Estudo sobre Justificação',
                createdAt: '2024-12-28T10:00:00Z',
                updatedAt: '2024-12-28T10:30:00Z',
                messages: [],
                isActive: true
            },
            {
                id: 'j9d3k-vs6hc1mb8fy',
                title: 'Significado de Ágape',
                createdAt: '2024-12-27T15:00:00Z',
                updatedAt: '2024-12-27T15:45:00Z',
                messages: [],
                isActive: false
            },
            {
                id: 'p2r7n-lg5tx9ub4wk',
                title: 'Escatologia em Daniel',
                createdAt: '2024-12-26T09:00:00Z',
                updatedAt: '2024-12-26T10:00:00Z',
                messages: [],
                isActive: false
            }
        ];

        return of({ conversations, total: conversations.length });
    }

    /**
     * Get mock projects for development
     */
    getMockProjects(): Observable<TeoProjectsResponse> {
        const projects: TeoProject[] = [
            {
                id: 'f84mv-ekd7jvs9844',
                title: 'Estudo de Romanos',
                description: 'Análise profunda da carta aos Romanos',
                category: 'soteriologia',
                icon: 'menu_book',
                gradient: 'from-blue-600 to-blue-800',
                conversations: [],
                sourceIds: ['1', '3'],
                sourcesCount: 2,
                createdAt: '2024-12-20T10:00:00Z',
                updatedAt: '2024-12-28T10:30:00Z'
            },
            {
                id: 'k2n9p-xt5mq8wa3pl',
                title: 'Doutrina da Trindade',
                description: 'Estudo sobre a natureza trina de Deus',
                category: 'teologia_propria',
                icon: 'auto_awesome',
                gradient: 'from-purple-600 to-purple-800',
                conversations: [],
                sourceIds: ['3', '6'],
                sourcesCount: 2,
                createdAt: '2024-12-15T09:00:00Z',
                updatedAt: '2024-12-27T15:45:00Z'
            },
            {
                id: 'w7h4c-bn2vr6yk9qs',
                title: 'Apocalipse e Escatologia',
                description: 'Estudo sobre os últimos tempos',
                category: 'escatologia',
                icon: 'schedule',
                gradient: 'from-amber-600 to-orange-700',
                conversations: [],
                sourceIds: ['1', '5'],
                sourcesCount: 2,
                createdAt: '2024-12-10T14:00:00Z',
                updatedAt: '2024-12-26T10:00:00Z'
            }
        ];

        return of({ projects, total: projects.length });
    }

    /**
     * Mock ask response for development
     */
    mockAsk(request: TeoAskRequest): Observable<TeoAskResponse> {
        const response: TeoAskResponse = {
            id: Date.now().toString(),
            conversationId: request.conversationId || 'new',
            answer: `Esta é uma resposta simulada para a pergunta: "${request.question}"\n\nA justificação pela fé é um dos pilares centrais da teologia protestante. Conforme Wayne Grudem explica em sua Teologia Sistemática, a justificação é o ato instantâneo de Deus pelo qual Ele declara nossos pecados perdoados e nos considera justos aos Seus olhos.\n\nMatthew Henry, em seu comentário sobre Romanos 3:28, ressalta que "somos justificados pela fé, sem as obras da lei" - isso não significa que as obras são dispensáveis, mas que não são a base da nossa justificação.`,
            citations: [
                {
                    sourceId: '3',
                    sourceName: 'Teologia Sistemática - Wayne Grudem',
                    excerpt: 'A justificação é o ato instantâneo de Deus pelo qual Ele (1) declara nossos pecados perdoados e a justiça de Cristo como nos pertencendo, e (2) nos declara justos aos Seus olhos.',
                    chapter: 'Capítulo 36'
                },
                {
                    sourceId: '1',
                    sourceName: 'Comentário Bíblico Matthew Henry',
                    excerpt: 'Somos justificados pela fé, sem as obras da lei. A fé é o instrumento pelo qual recebemos a justiça de Cristo.',
                    chapter: 'Romanos 3:28'
                }
            ],
            suggestedFollowups: [
                'Qual a diferença entre justificação e santificação?',
                'O que Paulo quis dizer com "obras da lei"?',
                'Como a justificação se relaciona com a eleição?'
            ],
            success: true
        };

        return of(response);
    }

    // ================================
    // PROJECTS API
    // ================================

    /**
     * Get all projects
     */
    getProjects(): Observable<TeoProject[]> {
        return this._httpClient.get<TeoProject[]>(`${this.baseUrl}/projects`);
    }

    /**
     * Get project by ID
     */
    getProject(id: string): Observable<TeoProject> {
        return this._httpClient.get<TeoProject>(`${this.baseUrl}/projects/${id}`);
    }

    /**
     * Create a new project
     */
    createProject(project: Partial<TeoProject>): Observable<TeoProject> {
        return this._httpClient.post<TeoProject>(`${this.baseUrl}/projects`, project);
    }

    /**
     * Update a project
     */
    updateProject(id: string, project: Partial<TeoProject>): Observable<TeoProject> {
        return this._httpClient.patch<TeoProject>(`${this.baseUrl}/projects/${id}`, project);
    }

    /**
     * Delete a project
     */
    deleteProject(id: string): Observable<void> {
        return this._httpClient.delete<void>(`${this.baseUrl}/projects/${id}`);
    }

    // ================================
    // TEO VISIBILITY CONTROL
    // ================================

    /**
     * Observable to track Teo open/close state
     */
    get isOpen$(): Observable<boolean> {
        return this._isOpen.asObservable();
    }

    /**
     * Open Teo and navigate to home
     */
    open(): void {
        // Save current URL before opening
        const currentUrl = this._router.url;
        if (!currentUrl.startsWith('/teo') && currentUrl !== '/' && currentUrl !== '') {
            this._previousUrl = currentUrl;
        }

        // Navigate to Teo (will trigger isOpen via route listener)
        this._router.navigate(['/teo']);
    }

    /**
     * Close Teo and navigate back to home
     */
    close(): void {
        // Always navigate to /home when closing
        this._router.navigate(['/home']);
    }
}
